// This function generates a properly formatted Mixpanel event name based on the enum values defined below.
export const formatMixpanelEventName = (
  page: MIXPANEL_PAGE | string,
  object: MIXPANEL_OBJECT,
  action: MIXPANEL_ACTION,
) => {
  return `${page} - ${object} ${action}`;
};

export enum MIXPANEL_PAGE {
  HOMEPAGE = "Homepage",
  MISSING_PAGE = "404 Page",
  WOMENS_PELVIC_HEALTH_PAGE = "Women's Pelvic Health Member",
  PREVENTION_PAGE = "Prevention",
  MENS_HEALTH_PAGE = "Mens Health",
  MEDICARE_ADVANTAGE_PAGE = "Medicare Advantage",
  ABOUT_PAGE = "About",
  PRODUCT_PAGE = "Product Page",
  DMC_FULL_PAGE = "DMC Full",
  CANADA = "Canada",
  CHRONIC_PAGE = "Chronic",
  BALANCE_PAGE = "Balance",
  VIP_PAGE = "VIP",
  ESI_PAGE = "ESI",
  EMO_PAGE = "EMO",
  ENSO_PAGE = "ENSO",
  FIND_PAGE = "FIND",
  HOUSE_CALL_PAGE = "House Call",
  FOR_INDIVIDUALS_PAGE = "For Individuals",
  FOR_INDIVIDUALS_SELECT_COMPANY_PAGE = "For Individuals Select Company",
  FOR_INDIVIDUALS_WOMENS_PELVIC_HEALTH_PAGE = "For Individuals Women's Pelvic Health",
  FOR_INDIVIDUALS_MIGRAINE_CARE_PAGE = "For Individuals Migraine Care",
  FOR_MEMBERS = "For Members",
  FOR_MEMBERS_SEO_SUPPORT_PAGE = "For Members Support Seo",
  FOR_ORGANIZATIONS = "For Organizations",
  RESOURCES = "Resources",
  ARTICLE = "Article",
  CAREERS = "Careers",
  CONTACT = "Contact",
  LEGAL = "Legal",
  DEI = "DEI",
  PRESS = "Press",
  CHAT = "Chat",
  CHAT_WIDGET = "Chat Widget",
  MOVEMENT_CONFERENCE = "Movement Conference Content Hub",
  ACQUISITION_PAGE = "Acquisition",
  GLOSSARY_PAGE = "Glossary",
  WOMENS_HEALTH_PAGE = "Womens Health Page",
  WOMENS_HEALTH_PAGE_WPH = "Womens Health Page WPH",
  PLAYLIST = "Playlist Page",
  ORTHO_CONSULTS = "Ortho Consults",
  MIGRAINE_CARE_PAGE = "Migraine Care",
  UNITED_KINGDOM = "United Kingdom",
  FRANCE = "France",
  NETHERLANDS = "Netherlands",
  GERMANY = "Germany",
  IRELAND = "Ireland",
  GLOBAL_WELLNESS_MEXICO = "Global Wellness Mexico",
  GLOBAL_WELLNESS_INDIA = "Global Wellness India",
  GLOBAL_WELLNESS_PAGE = "Global Wellness",
  DMC_FULL_PAGE_VARIANT_A = "DMC Full Page Variant A",
  DMC_FULL_PAGE_VARIANT_B = "DMC Full Page Variant B",
  DMC_ENSO_FULL_PAGE_VARIANT_A = "DMC Enso Full Page Variant A",
  DMC_ENSO_FULL_PAGE_VARIANT_B = "DMC Enso Full Page Variant B",
}

/*
  https://hingehealth.atlassian.net/browse/MWEB-1674

  All pages now report a page view event to Mixpanel via the ApplyMixpanelHook component, which wraps every page. 
  This map is for pages that had an explicitely set name before this was implemented.
  New pages will automatically have a name inferred by its path.
  If you do want to change the name of the page for a particular path for Mixpanel reporting add it to this object.
*/

export const MIXPANEL_CUSTOM_PAGE_VIEW = [
  "/for/",
  "/articles/",
  "/about/",
  "/press/",
  "/resources/",
  "/not-found/",
  "/select-company/",
  "/for-individuals/",
  "/for-organizations/",
  "/playlist/",
];

interface PageLookup {
  [name: string]: string;
}

export const MIXPANEL_PATH_PAGE_NAME: PageLookup = {
  "/": "Homepage",
  "/404/": "404 Page",
  "/for-individuals/pelvic-health/": "For Individuals Women's Pelvic Health",
  "/for-individuals/migrainecare/": "For Individuals Migraine Care",
};

export enum MIXPANEL_OBJECT {
  FILTER = "Filter",
  PAGE = "Page",
  CTA = "CTA",
  FAQ = "FAQ",
  TESTIMONIAL = "Testimonial Video",
  SEARCH_BAR = "Search Bar",
  CLIENT_SEARCH = "Client Search",
  V3_CLIENT_SEARCH = "V3 Client Search",
  CLIENT_SEARCH_NOT_FOUND = "Client Search Not Found",
  CHECKBOX = "CHECKBOX",
  COMPANY_RESULT = "Company Result",
  LANGUAGE_DROPDOWN = "Language Dropdown",
  CAROUSEL = "Carousel",
  CAROUSEL_SECTION = "Carousel Section",
  VIDEO_SECTION = "Video Section",
  SUBSCRIBE_FORM = "Subscribe Form",
  INPUT = "Input",
  APP_TOUR_SLIDE = "App Tour Slide",
  APP_TOUR_CTA = "App Tour CTA",
  APP_TOUR_NAV_BTN = "App Tour Nav Button",
  FUZZY_SEARCH_RESULT = "Fuzzy Search Result",
  SEARCH_NOT_FOUND = "Search Not Found",
  FLEXIBLE_CARD = "Flexible Card",
  PAGINATION = "Pagination",
  CHAT_WIDGET = "Chat Widget",
  BUTTON_FORM_BUTTON = "Button Form Button",
  BUTTON_FORM_OPTION = "Button Form Option",
  PLAYLIST_VIDEO = "Webplaylist",
  TABLE_OF_CONTENTS = "Table of Contents",
  QUIZ = "Quiz",
  MODAL = "Modal",
  LIGHTBOX = "Lightbox",
  DID_YOU_KNOW = "Did You Know",
  ARTICLE_NAV_DISMISSIBLE_TEXT = "Article Nav Dismissible Text",
  ARTICLE_NAV_DISMISSIBLE_BUTTON = "Article Nav Dismissible Button",
  DROPDOWN = "Dropdown",
  CONTACT_FORM = "Contact Form",
  INDIVIDUAL_ELIGIBILITY_CHECK = "Individual Eligibility Check",
  NEW_TESTIMONIAL = "New Testimonial",
  B2B_FORM = "B2B Form",
  SOCIAL_PROOF = "Social Proof",
  SIMPLE_BANNER = "Simple Banner",
  CTA_SCROLL_NOT_FOUND = "CTA Scroll Not Found",
  ADAPTIVE_LOADING = "Adaptive Loading",
}

export enum MIXPANEL_ACTION {
  LOADED = "Loaded",
  CLICKED = "Clicked",
  TOGGLED = "Toggled",
  SUBMITTED = "Submitted",
  INITIATED = "Initiated",
  ENTERED = "Entered",
  PLAYED = "Played",
  CLOSED = "Closed",
  VIDEO_SELECTED = "Video Selected",
  VIDEO_STARTED = "Video Started",
  VIDEO_COMPLETED = "Video Completed",
  VIDEO_CLOSED = "Video Closed",
  SWIPE_LEFT = "Swipe left",
  SWIPE_RIGHT = "Swipe right",
  MOVE_RIGHT = "Move right",
  MOVE_LEFT = "Move left",
  HOVER = "hover",
  PERFORMANCE_MEASURED = "Performance Measured",
  ACTIVATED = "Activated",
  DEACTIVATED = "Deactivated",
  GOT_IT_CLICKED = "Exited",
}

export enum MIXPANEL_URL_PARAM {
  UTM_TEMPLATE = "utm_template",
  UTM_SOURCE = "utm_source",
  UTM_MEDIUM = "utm_medium",
  UTM_CAMPAIGN = "utm_campaign",
  UTM_CONTENT = "utm_content",
  UTM_TERM = "utm_term",
  UTM_REF_SOURCE = "utm_ref_source",
  SEQUENCE = "sequence",
  EXP_NAME = "exp_name",
  EXP_VARIANT = "exp_variant",
  MARKETING_ID = "marketing_id",
  MAILFORMAT = "mailformat",
  AUD_SEGMENT = "aud_segment",
  TEMPLATE_NAME = "template_name",
  TEMPLATE_ID = "template_id",
  EXPERIENCE = "experience",
  OBILITY_ID = "obility_id",
  OBILITY_OFFER = "obility_offer",
  SPLIT_KEY = "split_key",
  SPLIT_VARIATION = "split_variation",
  SPLIT_KEY1 = "split_key1",
  SPLIT_VARIATION1 = "split_variation1",
  SPLIT_KEY2 = "split_key2",
  SPLIT_VARIATION2 = "split_variation2",
  DEEPLINK_SOURCE = "deeplink_source",
  RCODE = "rcode",
}

type CommonProperties = {
  sequence?: string;
  exp_name?: string;
  exp_variant?: string;
  marketing_id?: string;
  mailformat?: string;
  aud_segment?: string;
  template_name?: string;
  template_id?: string;
  experience?: string;
  utm_template?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  utm_ref_source?: string;
  obility_id?: string;
  obility_offer?: string;
  deeplink_source?: string;
  rcode?: string;
  split_key?: string;
  split_variation?: string;
  split_key1?: string;
  split_variation1?: string;
  split_key2?: string;
  split_variation2?: string;
};

export type MixpanelProperties = CommonProperties & {
  "Link Destination"?: string;
  "Component Clicked"?: string;
  "Search Term"?: string;
  "Company Clicked"?: string;
  "List Position"?: string;
  "Show Search Bar"?: boolean;
  "Is Form Valid"?: boolean;
  "Component Id"?: string; // The internal Id used for the component, not to be confused with the Contentful ID.
  Name?: string;
  Type?: string;
  Audience?: string[];
  Tags?: string[];
  Client?: string; // adds the client slug in cases such as WPH page where multiple clients use the same page
  incentive_result?: string | null;
  hh_client_id_ctx?: string;
  is_fallback?: boolean;
  metadata?: Record<string, string | boolean>;
  user_locale?: string;
  selected_locale?: string;
  platform?: string;
  text?: string;
  Question?: string;
  Selected?: string;
  "Current Slide"?: string | number;
  "Total Slides"?: string | number;
  "Button Type"?: string;
  "Pagination Index"?: string | number;
  "Filters Applied"?: boolean;
  "Glossary Term"?: string;
  "Selected Filter"?: string;
  "Button Form Option"?: string;
  "Web playlist - Video Select"?: string;
  "Web playlist - Video Start"?: string;
  "Web playlist - Video Complete"?: string;
  "Anchor Type"?: string;
  "Selected Video"?: string;
  "Article Title"?: string;
  "Link Text"?: string;
  "Did You Know"?: string;
  lightbox_name?: string;
  incentive_banner_type?: string;
  "Keyboard Event"?: string;
  "Question number"?: string;
  "Correct/Incorrect"?: string;
  "Dismiss Reason"?: string;
  "Failure Reason"?: string;
  eligibility_request_id?: string | null;
  "Correct Answers"?: string;
  "Total Questions"?: string;
  "Quiz Flow Completed"?: boolean;
  // Performance metrics
  lcp_value?: number;
  page_visible_on_load?: boolean;
  measurement_type?:
    | "measured"
    | "timeout_fallback"
    | "measurement_failed"
    | "tracking_failed";
  error_message?: string;
  DMCPageVariant?: string;
  Variant?: string;
  "Lite Mode Reason"?: string;
  "Lite Mode Source"?: string;
  adaptive_loading_mode?: string;
  adaptive_loading_reason?: string;
};

export type MixpanelLastTouchProperties = {
  [P in keyof CommonProperties as `${P} [last touch]`]?: CommonProperties[P];
};

export type MixpanelFirstTouchProperties = {
  [P in keyof CommonProperties as `${P} [first touch]`]?: CommonProperties[P];
};
